import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress,
  Paper,
  IconButton,
  TextField
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { uploadImage } from '../../services/contentService';

const ImageUploader = ({ currentImageUrl, onImageUploaded, folder = 'general' }) => {
  console.log('[ImageUploader] Initial onImageUploaded prop:', onImageUploaded, 'Type:', typeof onImageUploaded);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl || '');
  const [manualUrl, setManualUrl] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const processAndUploadFile = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      // Create a local preview
      const localPreview = URL.createObjectURL(file);
      setPreviewUrl(localPreview);

      // Upload to server
      const formData = new FormData();
      formData.append('image', file); // 'image' is a common key, backend should expect this
      formData.append('folder', folder); // Send folder name to backend

      const response = await uploadImage(formData);
      const uploadedUrl = response.imageUrl; // Assuming response is { imageUrl: '...' }
      
      // Update parent component
      if (uploadedUrl) {
        console.log('[ImageUploader] Uploaded URL:', uploadedUrl);
        console.log('[ImageUploader] About to call onImageUploaded. Type:', typeof onImageUploaded, 'Value:', onImageUploaded);
        onImageUploaded(uploadedUrl); // Corrected prop name
      } else {
        throw new Error('Image URL not found in response');
      }
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      // Revert to previous image if upload fails
      setPreviewUrl(currentImageUrl || '');
      if (typeof onImageUploaded === 'function') {
        onImageUploaded(currentImageUrl || ''); // Also revert in parent
      } else {
        console.error('[ImageUploader] onImageUploaded is not a function during error handling. Cannot revert in parent.');
      }
    } finally {
      setUploading(false);
    }
  };

  // This is the new main upload logic, keep this one.

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      processAndUploadFile(file);
    }
  };

  // Drag and Drop Handlers
  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDraggingOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      processAndUploadFile(file);
    }
  };

  // Restore the original handleRemoveImage and handleManualUrlSubmit functions
  const handleRemoveImage = () => {
    setPreviewUrl('');
    if (typeof onImageUploaded === 'function') {
      onImageUploaded(''); // Ensure correct prop name
    } else {
      console.error('[ImageUploader] onImageUploaded is not a function in handleRemoveImage.');
    }
  };

  const handleManualUrlChange = (e) => {
    setManualUrl(e.target.value);
  };

  const handleManualUrlSubmit = () => {
    if (manualUrl.trim()) {
      setPreviewUrl(manualUrl);
      if (typeof onImageUploaded === 'function') {
        onImageUploaded(manualUrl); // Ensure correct prop name
      } else {
        console.error('[ImageUploader] onImageUploaded is not a function in handleManualUrlSubmit.');
      }
      setManualUrl('');
    }
  };

  return (
    <Box>
      {previewUrl ? (
        <Box sx={{ mb: 2 }}>
          <Paper 
            sx={{ 
              p: 1, 
              position: 'relative',
              '&:hover .delete-button': {
                opacity: 1
              }
            }}
          >
            <Box 
              component="img"
              src={previewUrl}
              alt="Uploaded image"
              sx={{ 
                width: '100%',
                maxHeight: '200px',
                objectFit: 'contain'
              }}
            />
            <IconButton 
              className="delete-button"
              color="error"
              onClick={handleRemoveImage}
              sx={{ 
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                opacity: 0,
                transition: 'opacity 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Paper>
          <Typography variant="caption" color="textSecondary">
            {previewUrl.startsWith('blob:') ? 'Preview (uploading...)' : previewUrl}
          </Typography>
        </Box>
      ) : (
        <Box 
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{ 
            border: isDraggingOver ? '2px dashed #007bff' : '2px dashed #ccc',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            mb: 2,
            backgroundColor: isDraggingOver ? '#e7f3ff' : '#f9f9f9',
            transition: 'border-color 0.3s, background-color 0.3s'
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1" gutterBottom>
            Drag & drop an image here, or click to select
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Recommended size: 1200 x 600px, Max size: 5MB
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="contained"
          component="label"
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
        >
          {uploading ? 'Uploading...' : 'Upload Image'}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
        </Button>

        {previewUrl && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleRemoveImage}
            sx={{ ml: 1 }}
          >
            Remove
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Or enter image URL:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="https://example.com/image.jpg"
            value={manualUrl}
            onChange={handleManualUrlChange}
          />
          <Button 
            variant="outlined" 
            onClick={handleManualUrlSubmit}
            disabled={!manualUrl.trim()}
          >
            Use URL
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ImageUploader;
